import type { QueryClient } from '@tanstack/react-query';
import { LS_KEYS, removeFromLocalStorage } from './window';

/**
 * Guards duplicate logout cleanup. Cleared on the next microtask so a second
 * synchronous call in the same logout burst (middleware, then the coordinator)
 * only cancels in-flight traits refetches instead of repeating full cleanup.
 */
let isSegmentationLogoutCleanupInProgress = false;

const segmentationLogoutListeners = new Set<() => void>();

export const subscribeToSegmentationLogout = (listener: () => void): () => void => {
  segmentationLogoutListeners.add(listener);
  return () => {
    segmentationLogoutListeners.delete(listener);
  };
};

export const notifySegmentationLogoutListeners = (): void => {
  segmentationLogoutListeners.forEach((listener) => {
    listener();
  });
};

export const clearSegmentationOnLogout = (queryClient: QueryClient): void => {
  if (isSegmentationLogoutCleanupInProgress) {
    queryClient.cancelQueries({ queryKey: ['traits'] });
    return;
  }

  isSegmentationLogoutCleanupInProgress = true;

  queryClient.cancelQueries({ queryKey: ['traits'] });
  queryClient.removeQueries({ queryKey: ['traits'] });
  /*
   * TODO: remove this line after 01-04-2027. This entry is no longer in use, but we can keep the functionality that
   *  clears it for a while so existing entries are cleaned up
   */
  removeFromLocalStorage(LS_KEYS.SEGMENT_TRAITS);
  removeFromLocalStorage(LS_KEYS.SEGMENTATION_DATA);

  queueMicrotask(() => {
    isSegmentationLogoutCleanupInProgress = false;
  });
};

/** @internal Resets the logout cleanup guard between tests. */
export const resetSegmentationLogoutCleanupForTests = (): void => {
  isSegmentationLogoutCleanupInProgress = false;
  segmentationLogoutListeners.clear();
};
